<template>
  <div id="PERSISTENT_CONFIRMATIONS">
    <div class="container-fixed offset-top-2 offset-bottom-2">
      <span v-html="$t('persistentconfirmations.description_text')"><!-- #S927 --></span>

      <VSearchFormStatus v-bind="searchform" spinner class="offset-top-1" />
    </div>
    <template v-if="isTableFilled">
      <section class="container-fluid grayed-offset">
        <div class="container-fixed">
          <div class="row">
            <div class="col-l-8 col-s-12">
              <h3 id="PERSISTENT_CONFIRMATIONS_OVERVIEW">
                {{ $t('persistentconfirmations.overview_headline') }}
                <!-- #S908 -->
              </h3>
            </div>
            <div class="col-l-4 col-m-12 barring-actions pull-right offset-top-2">
              <transition name="fade" mode="out-in" appear>
                <VSpinner v-if="isBusy" key="busy" slim class="pull-right" />
                <div v-else-if="isNoticeVisible" key="notice" class="notification notification-warning">
                  <span role="status" aria-live="polite" v-html="$t('persistentconfirmations.overview_modifyto_' + noticeType)" />
                  <button
                    type="button"
                    class="close"
                    :title="$t('persistentconfirmations.informationBox_closeButton_tooltip')"
                    @click="isNoticeVisible = false"
                  >
                    <VSvgIcon name="cancel" variant="outlined" size="middle" />
                    <span class="sr-only">{{ $t('persistentconfirmations.informationBox_closeButton_tooltip') }}</span>
                  </button>
                </div>
              </transition>
            </div>
          </div>
          <p />
          <transition name="fade" mode="out-in" appear>
            <VueGoodTable v-bind="table">
              <template #table-row-after="{ row, index }">
                <td class="actions">
                  <VButton
                    class="btn-default btn-small"
                    label="persistentconfirmations.overview_details_button_label"
                    :disabled="isBusy"
                    @click.stop="selectRow(row, index)"
                  />
                  <VButton
                    v-if="isAllowedToModify && (row.status === 'ACTIVE' || row.status === 'TO_BE_DELETED')"
                    class="offset-left-1 btn-default btn-small"
                    :label="modifyLabel(row.status)"
                    :disabled="isBusy"
                    @click.stop="modify(row)"
                  />
                </td>
              </template>
              <template #table-column="{ column }">
                <VTableHeader :columns="table.columns" :column="column" locale-namespace="persistentconfirmations" />
              </template>
              <div slot="emptystate" class="center-align text-disabled">
                {{ $t('common.errorNoData') }}
              </div>
            </VueGoodTable>
          </transition>
        </div>
      </section>

      <section v-if="activeElement" id="PERSISTENT_CONFIRMATIONS_DETAILS" class="container-fixed offset-bottom-4">
        <VTabset v-if="isExpertView">
          <template #label>
            <transition name="fade" mode="out-in" appear>
              <span :key="activeElement.id">{{ $tc('persistentconfirmations.details_for_id', activeElement.id) }}</span>
            </transition>
            <!-- #S951 -->
          </template>
          <VTab label="persistentconfirmations.link_customer" name="customer">
            <transition name="fade" mode="out-in" appear>
              <VCustomerDetails
                :key="id"
                :partner-details="partnerDetails"
                :active-element="activeElement"
                :maximum-time-frame="maximumTimeFrame"
                class="offset-top-1"
              />
            </transition>
          </VTab>
          <VTab label="persistentconfirmations.link_expert" name="expert" active>
            <transition name="fade" mode="out-in" appear>
              <VExpertDetails
                :key="id"
                :partner-details="partnerDetails"
                :active-element="activeElement"
                :maximum-time-frame="maximumTimeFrame"
                class="offset-top-1"
              />
            </transition>
          </VTab>
        </VTabset>
        <template v-else>
          <h3 class="offset-bottom-2">
            {{ $t('persistentconfirmations.details_headline') }}
            <!-- #S928 -->
          </h3>

          <transition name="fade" mode="out-in" appear>
            <VCustomerDetails :key="id" :partner-details="partnerDetails" :active-element="activeElement" :maximum-time-frame="maximumTimeFrame" />
          </transition>
        </template>
      </section>
    </template>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { PERSISTENT_CONFIRMATIONS, PERSISTENT_CONFIRMATIONS_DETAILS } from '@route-types';
import VSpinner from '@/components/Spinner';
import VSearchFormStatus from '@/components/SearchFormStatus';
import TitleMixin from '@/mixins/title';
import SearchFormMixin from '@/mixins/searchform';
import TableMixin from '@/mixins/table';
import TableUtilitiesMixin from '@/mixins/tableUtilities';
import { formatDateTime } from '@/utils/date';
import FormatCurrencyMixin from '@/mixins/formatCurrency';
import Proxy from '@/proxies/PersistentConfirmationsProxy';
import PaymentTransactionsProxy from '@/proxies/PaymentTransactionsProxy';
import VButton from '@/components/Button';
import VSvgIcon from '@/components/UI/VSvgIcon.vue';

export default {
  name: 'PersistentConfirmations',

  components: {
    VSvgIcon,
    VSpinner,
    VueGoodTable: () => import('vue-good-table').then((m) => m.VueGoodTable),
    VButton,
    VSearchFormStatus,
    VTabset: () => import('@/components/UI/Tabs/Tabset'),
    VTab: () => import('@/components/UI/Tabs/Tab'),
    VCustomerDetails: () => import('./CustomerDetails'),
    VExpertDetails: () => import('./ExpertDetails'),
    VTableHeader: () => import('@/components/Table/Header'),
  },

  mixins: [FormatCurrencyMixin, TitleMixin, SearchFormMixin, TableMixin, TableUtilitiesMixin],

  props: {
    id: {
      type: Number,
      required: false,
      default: null,
    },
  },

  data() {
    return {
      isBusy: false,
      partnerDetails: null,
      table: {
        rows: [],
        columns: [
          {
            label: 'persistentconfirmations.overview_creation_date_column_heading',
            field: 'tableCreationDate',
            filterable: true,
            sortable: true,
            placeholder: ' ',
          },
          {
            label: 'persistentconfirmations.overview_provider_column_heading',
            field: 'bpName',
            filterable: true,
            sortable: true,
            placeholder: ' ',
          },
          {
            label: 'persistentconfirmations.overview_service_column_heading',
            field: 'serviceDescription',
            filterable: true,
            sortable: true,
            placeholder: ' ',
          },
          {
            label: 'persistentconfirmations.overview_price_column_heading',
            field: 'tableGrossAmount',
            filterable: true,
            sortable: true,
            placeholder: ' ',
            type: 'number',
          },
          {
            label: 'persistentconfirmations.overview_deletion_column_heading',
            field: 'tableDeletionDate',
            filterable: true,
            sortable: true,
            placeholder: ' ',
          },
          {
            label: 'persistentconfirmations.overview_status_column_heading',
            field: 'tableStatus',
            filterDropdown: true,
            filterOptions: ['NEW_LOCKED', 'CANCELLED', 'ACTIVE', 'ACTIVE_LOCKED', 'TO_BE_DELETED'].map(this.translateStatus),
            filter: this.tableColumnDropDownFilterFunction,
            filterable: true,
            sortable: true,
            placeholder: ' ',
          },
          {
            label: 'persistentconfirmations.overview_action_column_heading',
            field: '',
            filterable: false,
            sortable: false,
            placeholder: ' ',
          },
        ],
      },
      noticeType: null,
      isNoticeVisible: false,
      maximumTimeFrame: 0,
    };
  },

  computed: {
    ...mapGetters('system', {
      isExpertView: 'hasShowCCTransactionView',
      isAllowedToModify: 'isModifyPersistentConfirmationAllowed',
    }),

    isTableFilled() {
      return this.table.rows && this.table.rows.length;
    },

    activeElement() {
      return this.table?.rows?.find((i) => i.id === this.id);
    },
  },

  watch: {
    activeElement: {
      handler: 'loadDetails',
      immediate: true,
    },
  },

  async created() {
    this.searchLoading();
    const response = await new Proxy().all();

    if (response.length === 0) {
      this.searchErrorNoData();
    } else {
      this.table.rows = this.translateRows(response);
      this.handleDefaultSearchFields(this.table);
      this.searchSuccess();
    }

    this.isNoticeVisible = false;

    const paymentTransactionsResponse = await new PaymentTransactionsProxy().timeFrame();
    this.maximumTimeFrame = paymentTransactionsResponse.maximumTimeFrame;
  },

  methods: {
    translateRows(rows) {
      rows.map((row) => {
        if (row.creationDate) row.tableCreationDate = formatDateTime(row.creationDate);
        if (row.deletionDate) row.tableDeletionDate = formatDateTime(row.deletionDate);
        if (row.grossAmount) row.tableGrossAmount = this.formatCurrency(row.grossAmount, row.currency, 100);
        row.tableStatus = this.$tm('persistentconfirmations.status_', row.status);
        return row;
      });
      return rows;
    },

    selectRow(row, index) {
      this.setAriaSelected(index);
      const id = this.table?.rows[row.originalIndex]?.id;
      if (this.id === id) {
        this.$router.push({
          name: PERSISTENT_CONFIRMATIONS,
        });
      } else {
        this.$router.push({
          name: PERSISTENT_CONFIRMATIONS_DETAILS,
          params: {
            id,
            tab: this.$route.params.tab,
          },
        });
      }
    },

    async loadDetails() {
      if (!this.activeElement || this.isBusy) {
        this.partnerDetails = null;
        return;
      }
      this.partnerDetails = await new Proxy().partnerInfo(this.activeElement.bpNumber);
    },

    async modify(row) {
      this.isBusy = true;
      await new Proxy().modify(row.id, this.isToBeDeleted(row.status));
      this.switchStatus(row);
      this.showMessage(row.status);
      this.isBusy = false;
      this.isNoticeVisible = true;
    },

    modifyLabel(status) {
      if (this.isToBeDeleted(status)) {
        return 'persistentconfirmations.modify_to_be_deleted';
      } else {
        return 'persistentconfirmations.modify_to_active';
      }
    },

    showMessage(status) {
      if (this.isToBeDeleted(status)) {
        this.noticeType = 'active';
      } else {
        this.noticeType = 'tobedeleted';
      }
    },

    isToBeDeleted(status) {
      if (status === 'ACTIVE') {
        return true;
      } else if (status === 'TO_BE_DELETED') {
        return false;
      } else {
        throw new Error('invalidModifyStatus');
      }
    },

    switchStatus(row) {
      if (row.status === 'ACTIVE') {
        row.status = 'TO_BE_DELETED';
      } else if (row.status === 'TO_BE_DELETED') {
        row.status = 'ACTIVE';
      }
    },

    tableRowStyle(row) {
      if (this.id === row.id) {
        return 'brand';
      }
      return '';
    },

    translateStatus(value) {
      return this.$tm('persistentconfirmations.status_', value);
    },
  },
};
</script>

<style scoped>
.btn {
  margin-top: 6px;
  margin-bottom: 6px;
}

td.actions {
  white-space: nowrap;
}
</style>
